import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Stack,
  IconButton,
  InputAdornment,
} from "@mui/material";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Visibility from "@mui/icons-material/Visibility";

export default function SubscriberConfigureDialog({
  open,
  onClose,
  onSave,
  saving = false,
  mode = "add",
  initialData = null,
  productName = "AMS360",
  configurationFields = [],
}) {
  const resolvedFields = Array.isArray(configurationFields)
    ? configurationFields.filter((field) => field?.keyName)
    : [];

  const createBaseEditorState = () => {
    const fields = {};
    resolvedFields.forEach((field) => {
      fields[field.keyName] = field.keyValue ?? "";
    });

    return {
      configurationName: "",
      fields,
    };
  };

  const [editorState, setEditorState] = useState(createBaseEditorState);
  const [errors, setErrors] = useState({});
  const [visibleFields, setVisibleFields] = useState({});

  const toggleFieldVisibility = (keyName) => {
    setVisibleFields((prev) => ({ ...prev, [keyName]: !prev[keyName] }));
  };

  useEffect(() => {
    const nextState = createBaseEditorState();

    if (mode === "edit" && initialData) {
      const sourceFields = initialData.fields ?? {};
      resolvedFields.forEach((field) => {
        nextState.fields[field.keyName] =
          sourceFields[field.keyName] ??
          initialData[field.keyName] ??
          field.keyValue ??
          "";
      });
      nextState.configurationName = initialData.configurationName ?? "";
    }

    setEditorState(nextState);
    setErrors({});
  }, [open, mode, initialData, configurationFields]);

  const onEditorChange = (field) => (event) => {
    const value = event?.target?.value || "";
    setEditorState((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const onEditorFieldChange = (keyName) => (event) => {
    const value = event?.target?.value || "";
    setEditorState((prev) => ({
      ...prev,
      fields: {
        ...prev.fields,
        [keyName]: value,
      },
    }));
    if (errors[keyName]) {
      setErrors((prev) => ({
        ...prev,
        [keyName]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!editorState.configurationName.trim()) {
      newErrors.configurationName = "Configuration name is required";
    }

    resolvedFields.forEach((field) => {
      const keyName = field.keyName;
      const label = field.userLabel || field.keyName;
      const value = String(editorState.fields[keyName] || "").trim();
      if (!value) {
        newErrors[keyName] = `${label} is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave({
        configurationName: editorState.configurationName,
        fields: editorState.fields,
      });
    }
  };

  const handleClose = () => {
    setEditorState(createBaseEditorState());
    setErrors({});
    onClose();
  };

  const title = mode === "edit" ? "Edit Configuration" : "Add Configuration";
  const subtitle = `Configure credentials for ${productName ?? "AMS360"}`;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: 16, fontWeight: 600, color: "#2f3a4a" }}>
        {title}
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Typography
          variant="caption"
          sx={{ color: "#8b95a5", display: "block", mb: 2 }}
        >
          {subtitle}
        </Typography>

        <Stack spacing={2}>
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 0.5,
                fontWeight: 500,
              }}
            >
              Configuration Name <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter configuration name"
              value={editorState.configurationName}
              onChange={onEditorChange("configurationName")}
              error={!!errors.configurationName}
              helperText={errors.configurationName}
              variant="outlined"
            />
          </Box>

          {resolvedFields.map((field) => {
            const label = field.userLabel || field.keyName;
            const keyName = field.keyName;
            const isPasswordField = keyName.toLowerCase().includes("password");
            let inputType = "text";
            if (isPasswordField && !visibleFields[keyName]) {
              inputType = "password";
            }

            const visibilityIcon = visibleFields[keyName] ? (
              <VisibilityOff fontSize="small" />
            ) : (
              <Visibility fontSize="small" />
            );

            const passwordAdornment = (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => toggleFieldVisibility(keyName)}
                  edge="end"
                >
                  {visibilityIcon}
                </IconButton>
              </InputAdornment>
            );

            const textFieldInputProps = isPasswordField
              ? { endAdornment: passwordAdornment }
              : undefined;

            return (
              <Box key={keyName} sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  sx={{
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  {label} <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  type={inputType}
                  placeholder={`Enter ${label?.toLowerCase()}`}
                  value={editorState.fields[keyName] || ""}
                  onChange={onEditorFieldChange(keyName)}
                  variant="outlined"
                  size="small"
                  error={!!errors[keyName]}
                  helperText={errors[keyName]}
                  inputProps={{ readOnly: !!field.readOnly }}
                  InputProps={textFieldInputProps}
                />
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={saving}
          sx={{
            textTransform: "none",
            color: "#5f6b7a",
            borderColor: "#d9dee8",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
          sx={{ textTransform: "none" }}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

SubscriberConfigureDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  mode: PropTypes.oneOf(["add", "edit"]),
  initialData: PropTypes.shape({
    configurationName: PropTypes.string,
    fields: PropTypes.object,
  }),
  productName: PropTypes.string,
  configurationFields: PropTypes.arrayOf(
    PropTypes.shape({
      keyName: PropTypes.string,
      keyValue: PropTypes.string,
      userLabel: PropTypes.string,
      readOnly: PropTypes.bool,
    }),
  ),
};